/**
 * Момент времени в поясе ОРГАНИЗАЦИИ — для отпусков и отсутствий.
 *
 * Зачем это вообще нужно. Рабочие часы даты не имеют: у них только день недели и минуты
 * от полуночи, и пояс разворачивает сервер. Отпуск — абсолютный интервал, и его границы
 * уходят на бэкенд ISO-строкой. Отправь фронт `2026-09-08T00:00:00` без смещения — сервер
 * (`z.string().datetime({ offset: true })`) её просто отвергнет, а отправь он её с локальным
 * смещением ТЕЛЕФОНА, было бы хуже: владелец из Москвы закрыл бы ташкентскому мастеру день
 * с 02:00, оставив два утренних часа открытыми для записи (AGENTS.md, правило 1).
 *
 * Поэтому смещение считается для КОНКРЕТНОГО момента в КОНКРЕТНОМ поясе организации,
 * а не берётся у `Date` и не задаётся константой `+05:00`: `businesses.tz` — поле, и
 * организация в поясе с переводом стрелок обязана работать так же.
 *
 * Luxon во фронтенде нет намеренно: единственная нужная операция — «стенные часы в поясе
 * → момент», и она выражается через `Intl`, который в бандле уже есть. Библиотека ради
 * одной функции стоила бы больше, чем весит этот файл.
 */

import { parseDayKey, shiftDayKey, type DayKey } from '../../../domain/datetime';
import { formatHhMm, MINUTES_PER_DAY } from './week';

/**
 * Стенные часы момента в поясе, прочитанные обратно как UTC.
 *
 * Приём стандартный: разница между этим числом и самим моментом и есть смещение пояса
 * в эту секунду — вместе со всеми переводами стрелок и историческими правками базы IANA.
 */
function wallClockAsUtc(instant: Date, tz: string): number {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: tz,
    // h23, а не hour12: false: часть движков отдаёт для полуночи «24», и без явного цикла
    // сутки уезжали бы на день ровно в тот момент, который чаще всего и задают в отпуске.
    hourCycle: 'h23',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  }).formatToParts(instant);

  const get = (type: Intl.DateTimeFormatPartTypes): number => {
    const part = parts.find((item) => item.type === type);
    if (!part) throw new Error(`Intl не вернул часть даты «${type}» для пояса ${tz}`);
    const value = Number(part.value);
    if (!Number.isFinite(value)) {
      throw new Error(`Intl вернул нечисловую часть даты «${type}» для пояса ${tz}: ${part.value}`);
    }
    return value;
  };

  return Date.UTC(get('year'), get('month') - 1, get('day'), get('hour') % 24, get('minute'), get('second'));
}

/** Смещение пояса в минутах в заданный момент. Положительное к востоку от Гринвича. */
export function offsetMinutesAt(instant: Date, tz: string): number {
  return (wallClockAsUtc(instant, tz) - instant.getTime()) / 60_000;
}

/** «+05:00». Ноль печатается как `+00:00`, а не `Z`: одна форма читается проще двух. */
export function formatOffset(offsetMinutes: number): string {
  const sign = offsetMinutes < 0 ? '-' : '+';
  const absolute = Math.abs(Math.round(offsetMinutes));
  const hours = Math.floor(absolute / 60);
  const minutes = absolute % 60;
  return `${sign}${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
}

/**
 * День + минуты от полуночи В ПОЯСЕ ОРГАНИЗАЦИИ → ISO-строка со смещением.
 *
 * Смещение уточняется дважды: первое приближение берётся по стенному времени, второе —
 * уже по найденному моменту. Один проход ошибается на час у дат по другую сторону перевода
 * стрелок, а такая ошибка — это час, который остался открытым для записи в отпуске.
 */
export function zonedIso(day: DayKey, minutesFromMidnight: number, tz: string): string {
  if (
    !Number.isInteger(minutesFromMidnight) ||
    minutesFromMidnight < 0 ||
    minutesFromMidnight >= MINUTES_PER_DAY
  ) {
    // Полночь следующих суток задаётся следующим днём (`shiftDayKey`), а не «24:00»:
    // строка `2026-09-08T24:00:00+05:00` недопустима по ISO 8601 и её отвергнет сервер.
    throw new Error(`Минуты от полуночи вне суток: ${minutesFromMidnight}`);
  }

  const { year, month, day: dayOfMonth } = parseDayKey(day);
  const wall = Date.UTC(year, month - 1, dayOfMonth, 0, minutesFromMidnight);

  const firstGuess = offsetMinutesAt(new Date(wall), tz);
  const refined = offsetMinutesAt(new Date(wall - firstGuess * 60_000), tz);

  return `${day}T${formatHhMm(minutesFromMidnight)}:00${formatOffset(refined)}`;
}

/** Полночь следующего дня — правая, ИСКЛЮЧАЮЩАЯ граница суток. */
export function endOfDayIso(day: DayKey, tz: string): string {
  return zonedIso(shiftDayKey(day, 1), 0, tz);
}

export interface TimeOffDraft {
  readonly fromDay: DayKey;
  readonly toDay: DayKey;
  /** Весь день: время не спрашивается, границы — полночь и полночь следующих суток. */
  readonly allDay: boolean;
  /** Минуты от полуночи. Учитываются только при `allDay: false`. */
  readonly fromMin: number;
  readonly toMin: number;
}

export interface TimeOffRange {
  readonly startsAt: string;
  readonly endsAt: string;
}

/**
 * Границы отсутствия. `null` — конец не позже начала, экран покажет это владельцу.
 *
 * Правая граница ИСКЛЮЧАЮЩАЯ: занятость сервер сравнивает полуинтервалами
 * (`overlaps` в `packages/backend/src/domain/time.ts`), поэтому «весь день 8 сентября» —
 * это `[8-е 00:00, 9-е 00:00)`. Вариант «до 23:59» оставил бы последнюю минуту суток
 * открытой — мелочь, пока шаг сетки не окажется минутным.
 */
export function timeOffRange(draft: TimeOffDraft, tz: string): TimeOffRange | null {
  const startsAt = zonedIso(draft.fromDay, draft.allDay ? 0 : draft.fromMin, tz);
  const endsAt = draft.allDay
    ? endOfDayIso(draft.toDay, tz)
    : zonedIso(draft.toDay, draft.toMin, tz);

  if (new Date(endsAt).getTime() <= new Date(startsAt).getTime()) return null;
  return { startsAt, endsAt };
}
